import { useEffect, useRef, useState } from 'react'

/**
 * Appareil photo qui reste ouvert entre les déclenchements.
 *
 * C'est la décision d'ergonomie no 6 : sur un mur de 30 mètres, revenir à la
 * grille entre chaque prise est intenable. Les notes ne sont pas demandées
 * ici — elles se posent ensuite, depuis la grille.
 */
export function CameraRafale({
  fermer,
  capturer
}: {
  fermer: () => void
  capturer: (fichiers: Blob[]) => void
}) {
  const video = useRef<HTMLVideoElement>(null)
  const flux = useRef<MediaStream | null>(null)
  const [prises, setPrises] = useState<{ blob: Blob; url: string }[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [pret, setPret] = useState(false)

  useEffect(() => {
    let annule = false
    navigator.mediaDevices
      ?.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 2560 } },
        audio: false
      })
      .then((s) => {
        if (annule) {
          s.getTracks().forEach((t) => t.stop())
          return
        }
        flux.current = s
        if (video.current) {
          video.current.srcObject = s
          void video.current.play()
        }
        setPret(true)
      })
      .catch(() => {
        setErreur(
          "Impossible d'ouvrir l'appareil photo. Vérifie l'autorisation dans les réglages du navigateur."
        )
      })
    return () => {
      annule = true
      flux.current?.getTracks().forEach((t) => t.stop())
    }
  }, [])

  const prisesRef = useRef(prises)
  prisesRef.current = prises
  useEffect(
    () => () => prisesRef.current.forEach((p) => URL.revokeObjectURL(p.url)),
    []
  )

  async function declencher() {
    const v = video.current
    if (!v || !v.videoWidth) return
    const toile = document.createElement('canvas')
    toile.width = v.videoWidth
    toile.height = v.videoHeight
    const ctx = toile.getContext('2d')
    if (!ctx) return
    ctx.drawImage(v, 0, 0)
    const blob = await new Promise<Blob | null>((r) => toile.toBlob(r, 'image/jpeg', 0.92))
    if (!blob) return
    setPrises((p) => [...p, { blob, url: URL.createObjectURL(blob) }])
    if (navigator.vibrate) navigator.vibrate(18)
  }

  function terminer() {
    capturer(prises.map((p) => p.blob))
  }

  return (
    <div className="camera">
      <div className="camera-vue">
        {erreur ? (
          <div className="camera-erreur">
            <p>{erreur}</p>
            <button className="bouton creux" onClick={fermer} style={{ marginTop: 14 }}>
              Revenir
            </button>
          </div>
        ) : (
          <video ref={video} playsInline muted />
        )}
      </div>

      {prises.length > 0 && (
        <div className="camera-bande">
          {prises.map((p, i) => (
            <img key={i} src={p.url} alt={`Prise ${i + 1}`} />
          ))}
        </div>
      )}

      <div className="camera-barre">
        <button className="camera-txt" onClick={fermer}>
          Annuler
        </button>
        <button
          className="camera-declencheur"
          onClick={() => void declencher()}
          disabled={!pret || !!erreur}
          aria-label="Prendre une photo"
        >
          <span />
        </button>
        <button className="camera-txt fort" onClick={terminer} disabled={prises.length === 0}>
          Terminé{prises.length > 0 ? ` · ${prises.length}` : ''}
        </button>
      </div>
    </div>
  )
}
